/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TK-39 §1 (lee 23/10) — trang **Cài đặt chiến lược** `/cai-dat-chien-luoc`: gộp Lịch đóng cắt + Chiến lược dim.
 *   · Thanh chọn Tuyến → Tủ đầu trang; tab "Đóng cắt cấp nguồn" | "Độ sáng đèn"; nhớ trong URL `?tab=dong-cat|do-sang&tuyen=&tu=`.
 *   · Đóng cắt = trang Lịch cũ (nhúng) + khối "Sinh từ mẫu…" (trang Chiến lược cũ, nhúng) + khối "Áp cho" chung.
 *   · Độ sáng đèn = 32 timer dim (TK-39 §3). Cờ NEMA tắt ⇒ tab này biến mất, trang chỉ còn Đóng cắt.
 *   · Không tự chọn tủ mặc định: chưa chọn ⇒ chưa có trang soạn (soạn nhầm tủ là thứ chỉ phát hiện ngoài đường).
 *   · `/lich`, `/lich/:id`, `/chien-luoc` chuyển hướng về đây (App.tsx).
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SdkdIcon } from '../components/icon/SdkdIcon';
import { Spinner } from '../components/ui';
import { useTree } from '../lib/queries';
import { useNemaBat } from '../lib/nema';
import { useDatDoiTuong } from '../lib/doiTuongDangChon';
import SchedulePage from './Schedule';
import StrategiesPage from './Strategies';
import { TabDoSang } from '../components/caidat/TabDoSang';

type Tab = 'dong-cat' | 'do-sang';
const TAB: { id: Tab; nhan: string }[] = [{ id: 'dong-cat', nhan: 'Đóng cắt cấp nguồn' }, { id: 'do-sang', nhan: 'Độ sáng đèn' }];

export default function CaiDatChienLuocPage() {
  const [sp, setSp] = useSearchParams();
  const tree = useTree();
  const coNema = useNemaBat().trangThai === 'bat';
  useEffect(() => { document.title = 'Cài đặt chiến lược — SDKD'; }, []);

  const tuyenDs = tree.data ?? [];
  const tuId = sp.get('tu');
  /* tủ cho trước mà thiếu tuyến (link cũ /lich/:id) ⇒ tuyến của tủ ấy */
  const tuyenId = sp.get('tuyen') ?? (tuId ? tuyenDs.find((r) => r.cabinets.some((c) => c.id === tuId))?.id ?? null : null);
  const tuyen = tuyenDs.find((r) => r.id === tuyenId) ?? null;
  const tu = tuyen?.cabinets.find((c) => c.id === tuId) ?? null;
  useDatDoiTuong({ tuyen: tuyen?.tenDayDu, tu: tu?.name });   // TK-45 §2: theo 2 ô chọn trên đầu trang
  const tab: Tab = coNema && sp.get('tab') === 'do-sang' ? 'do-sang' : 'dong-cat';
  const dat = (k: Record<string, string | null>) => {
    const n = new URLSearchParams(sp);
    for (const [a, b] of Object.entries(k)) { if (b === null) n.delete(a); else n.set(a, b); }
    setSp(n, { replace: true });
  };
  const mauMo = sp.get('mau') === '1';
  const khoiMau = useMemo(() => (
    <details className="rounded-xl border border-vien bg-nen" open={mauMo} data-o="sinh-tu-mau">
      <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-chu-1">Sinh từ mẫu…</summary>
      <div className="border-t border-vien p-3.5"><StrategiesPage nhung /></div>
    </details>
  ), [mauMo]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-nut bg-nhan/15 text-nhan"><SdkdIcon name="chien-luoc" className="h-5 w-5" /></span>
        <h1 className="chu-tieude m-0 text-[26px] font-semibold leading-tight text-chu-1">Cài đặt chiến lược</h1>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-o="chon-tuyen-tu">
        {tree.isLoading ? <Spinner /> : (
          <>
            <select aria-label="Tuyến" className="inp min-w-0 max-w-xs" value={tuyenId ?? ''} onChange={(e) => dat({ tuyen: e.target.value || null, tu: null })}>
              <option value="">— Chọn tuyến —</option>
              {tuyenDs.map((r) => <option key={r.id} value={r.id}>{r.tenDayDu}</option>)}
            </select>
            <span className="text-chu-3">→</span>
            <select aria-label="Tủ" className="inp min-w-0 max-w-xs" value={tu?.id ?? ''} disabled={!tuyen} onChange={(e) => dat({ tu: e.target.value || null })}>
              <option value="">— Chọn tủ —</option>
              {(tuyen?.cabinets ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </>
        )}
      </div>

      {coNema && (
        <div role="tablist" className="flex gap-1 border-b border-vien" data-o="tab-cai-dat">
          {TAB.map((t) => (
            <button key={t.id} role="tab" type="button" aria-selected={tab === t.id} onClick={() => dat({ tab: t.id })} data-tab={t.id}
              className={`-mb-px min-h-[40px] border-b-2 px-3.5 text-sm font-semibold ${tab === t.id ? 'border-nhan text-chu-1' : 'border-transparent text-chu-2 hover:text-chu-1'}`}>
              {t.nhan}
            </button>
          ))}
        </div>
      )}

      {!tu && <p className="m-0 rounded-xl border border-vien bg-nen px-4 py-5 text-center text-sm text-chu-2" data-o="chua-chon-tu">Chọn tuyến và tủ</p>}
      {tab === 'dong-cat' && (tu ? <SchedulePage nhung={{ cabinetId: tu.id, tenTu: tu.name, truocKhoiCuoi: khoiMau }} /> : khoiMau)}
      {tab === 'do-sang' && tu && <TabDoSang key={tu.id} cabinetId={tu.id} routeId={tuyen?.id ?? null} tenTu={tu.name} />}
    </div>
  );
}
